/**
 * 应用全局状态（轻量 Context + useReducer，不引入 zustand）。
 *
 * 当前持有：
 *   - currentAccount    当前登录账号（id / displayName）
 *   - currentSpace      当前空间（id / adminId）
 *   - currentMemberKey  当前账号在当前空间下的 MemberKey（公钥、deviceId）
 *   - unreadMeetStoryUpdate  《相识相遇》未读更新（红点）
 */

import {
  createContext,
  createElement,
  useContext,
  useMemo,
  useReducer,
  type Dispatch,
  type ReactNode,
} from 'react';
import type { MemberKey } from '../../model/types';

/** 登录后可供各页面直接使用的账号摘要。 */
export interface CurrentAccount {
  id: string;
  displayName: string | null;
  maskedPhone?: string;
  avatarUrl?: string | null;
}

/** 空间信息 */
export interface CurrentSpace {
  id: string;
  adminId: string;
  startDate: string;
  role?: 'owner' | 'member';
}

/** 当前账号的 MemberKey（公钥 + deviceId） */
export interface CurrentMemberKeyInfo extends Pick<MemberKey, 'id' | 'publicKey' | 'deviceId' | 'keyVersion'> {}

/** Store 状态 */
export interface AppState {
  currentAccount: CurrentAccount | null;
  currentSpace: CurrentSpace | null;
  currentMemberKey: CurrentMemberKeyInfo | null;
  unreadMeetStoryUpdate: boolean;
}

/** Action 类型 */
export type AppAction =
  | { type: 'setAccount'; account: CurrentAccount | null }
  | { type: 'setSpace'; space: CurrentSpace | null }
  | { type: 'setMemberKey'; memberKey: CurrentMemberKeyInfo | null }
  | { type: 'setUnreadMeetStoryUpdate'; unread: boolean }
  | { type: 'reset' };

/** 初始状态 */
export const initialAppState: AppState = {
  currentAccount: null,
  currentSpace: null,
  currentMemberKey: null,
  unreadMeetStoryUpdate: false,
};

/** Reducer */
export function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'setAccount':
      return { ...state, currentAccount: action.account };
    case 'setSpace':
      return { ...state, currentSpace: action.space };
    case 'setMemberKey':
      return { ...state, currentMemberKey: action.memberKey };
    case 'setUnreadMeetStoryUpdate':
      return { ...state, unreadMeetStoryUpdate: action.unread };
    case 'reset':
      return initialAppState;
    default:
      return state;
  }
}

/** Context 类型 */
export interface AppStoreContextValue {
  state: AppState;
  dispatch: Dispatch<AppAction>;
}

const AppStoreContext = createContext<AppStoreContextValue | null>(null);

/** Provider 组件 */
export interface AppStoreProviderProps {
  children: ReactNode;
  /** 可选初始状态（用于测试） */
  initialState?: AppState;
}

export function AppStoreProvider({ children, initialState }: AppStoreProviderProps): ReactNode {
  const [state, dispatch] = useReducer(appReducer, initialState ?? initialAppState);
  const value = useMemo(() => ({ state, dispatch }), [state]);
  return createElement(AppStoreContext.Provider, { value }, children);
}

/** 取状态 + dispatch；不在 Provider 内时抛错。 */
export function useAppStore(): AppStoreContextValue {
  const ctx = useContext(AppStoreContext);
  if (!ctx) {
    throw new Error('state.store: useAppStore must be used within AppStoreProvider');
  }
  return ctx;
}

/** 仅读取状态（便于组件订阅） */
export function useAppState(): AppState {
  return useAppStore().state;
}

/** 页面级可选读取：允许独立渲染的搜索/预览组件在无 Provider 测试环境中工作。 */
export function useOptionalAppState(): AppState {
  return useContext(AppStoreContext)?.state ?? initialAppState;
}

/** 仅读取 dispatch */
export function useAppDispatch(): Dispatch<AppAction> {
  return useAppStore().dispatch;
}
